import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'framer-motion';
import { Truck, CheckCircle2, Package, Phone, MessageCircle } from 'lucide-react';
import { SiWhatsapp } from 'react-icons/si';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/hooks/useCart';
import { useOrders } from '@/hooks/useOrders';
import { formatPrice, ROUTE_PATHS, EGYPT_GOVERNORATES } from '@/lib/index';
import { springPresets } from '@/lib/motion';
import { useToast } from '@/hooks/use-toast';
import { trackInitiateCheckout, trackPurchase } from '@/lib/analytics';

const checkoutSchema = z.object({
  name: z.string().min(2, 'الاسم يجب أن يكون حرفين على الأقل'),
  phone: z.string().min(11, 'رقم الهاتف يجب أن يكون 11 رقم على الأقل'),
  city: z.string().min(2, 'يرجى اختيار المحافظة'),
  address: z.string().min(5, 'العنوان يجب أن يكون 5 أحرف على الأقل'),
  notes: z.string().optional(),
  paymentMethod: z.enum(['cash', 'vodafone', 'instapay'], {
    required_error: 'يرجى اختيار طريقة الدفع',
  }),
});

type CheckoutFormData = z.infer<typeof checkoutSchema>;

export default function Checkout() {
  const navigate = useNavigate();
  const { items, getTotal, clearCart } = useCart();
  const { createOrder, loading: orderLoading } = useOrders();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CheckoutFormData>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      paymentMethod: 'cash',
    },
  });

  const paymentMethod = watch('paymentMethod');
  const subtotal = getTotal();
  const shipping = 70; // قيمة الشحن الثابتة
  const total = subtotal + shipping;

  const whatsappNumber = '01113666594';
  const paymentNumber = '01060640403';

  // Track InitiateCheckout when page loads
  useEffect(() => {
    if (items.length > 0) {
      trackInitiateCheckout(items, total);
    }
  }, []);
  const onSubmit = async (data: CheckoutFormData) => {
    setIsSubmitting(true);

    try {
      // تحضير بيانات الطلب
      const orderData = {
        customer_name: data.name,
        customer_phone: data.phone,
        shipping_address: data.address,
        shipping_city: data.city,
        shipping_notes: data.notes,
        payment_method: data.paymentMethod,
        total_amount: total,
        shipping_fee: shipping,
        items: items.map(item => ({
          product_id: item.product.id,
          product_name: item.product.name,
          product_image: item.product.image,
          quantity: item.quantity,
          price: item.product.price,
          total: item.product.price * item.quantity
        }))
      };

      console.log('📦 Submitting order:', orderData);

      const result = await createOrder(orderData);

      if (result.success && result.order) {
        setOrderId(result.order.id);
        setOrderPlaced(true);
        clearCart();
        
        // Track Purchase event
        trackPurchase(result.order.id, items, total);
        
        toast({
          title: 'تم إنشاء الطلب بنجاح',
          description: `رقم الطلب: ${result.order.id}`,
        });
      } else {
        toast({
          title: 'خطأ',
          description: result.error || 'فشل في إنشاء الطلب',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      toast({
        title: 'خطأ',
        description: 'حدث خطأ أثناء إنشاء الطلب',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppContact = () => {
    const message = encodeURIComponent(
      `مرحباً! أريد الاستفسار عن طلبي.\n\nإجمالي الطلب: ${formatPrice(total)}\nعدد المنتجات: ${items.length}`
    );
    window.open(`https://wa.me/2${whatsappNumber}?text=${message}`, '_blank');
  };

  if (items.length === 0 && !orderPlaced) {
    return (
      <div className="container mx-auto px-4 py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="max-w-md mx-auto text-center"
          dir="rtl"
        >
          <Package className="w-16 h-16 mx-auto mb-6 text-muted-foreground" />
          <h2 className="text-2xl font-semibold mb-4">سلة التسوق فارغة</h2>
          <p className="text-muted-foreground mb-8">
            أضف بعض المنتجات إلى سلة التسوق قبل إتمام الطلب
          </p>
          <Button onClick={() => navigate(ROUTE_PATHS.PRODUCTS)} size="lg">
            تصفح المنتجات
          </Button>
        </motion.div>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="container mx-auto px-4 py-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={springPresets.bouncy}
          className="max-w-2xl mx-auto text-center"
          dir="rtl"
        >
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-primary" />
            </div>
            <h1 className="text-4xl font-bold mb-4">تم استلام طلبك بنجاح!</h1>
            <p className="text-xl text-muted-foreground mb-2">
              رقم الطلب: <span className="font-semibold text-foreground">{orderId}</span>
            </p>
            <p className="text-muted-foreground">
              سنتواصل معك قريباً لتأكيد الطلب
            </p>
          </div>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle>ملخص الطلب</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.product.id} className="flex gap-3 items-center">
                    <img
                      src={item.product.image}
                      alt={item.product.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 text-right">
                      <p className="font-medium">{item.product.name}</p>
                      <p className="text-sm text-muted-foreground">
                        الكمية: {item.quantity}
                      </p>
                    </div>
                    <p className="font-semibold">
                      {formatPrice(item.product.price * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">المجموع الفرعي</span>
                  <span className="font-medium">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">الشحن</span>
                  <span className="font-medium">{formatPrice(shipping)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg">
                  <span className="font-semibold">الإجمالي</span>
                  <span className="font-bold text-primary">{formatPrice(total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleWhatsAppContact}
              size="lg"
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white"
            >
              <SiWhatsapp className="w-5 h-5 ml-2" />
              تواصل عبر واتساب
            </Button>
            <Button
              onClick={() => navigate(ROUTE_PATHS.HOME)}
              variant="outline"
              size="lg"
            >
              العودة للرئيسية
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springPresets.gentle}
        dir="rtl"
      >
        <h1 className="text-4xl font-bold mb-2">إتمام الطلب</h1>
        <p className="text-muted-foreground mb-12">أكمل بياناتك لإتمام الطلب</p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* معلومات الشحن */}
              <Card>
                <CardHeader>
                  <CardTitle>معلومات الشحن</CardTitle>
                  <CardDescription>
                    أدخل معلومات التوصيل الخاصة بك
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* الاسم */}
                  <div className="space-y-2">
                    <Label htmlFor="name">الاسم الكامل *</Label>
                    <Input
                      id="name"
                      placeholder="أحمد محمد"
                      {...register('name')}
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  {/* رقم الهاتف */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">رقم الهاتف *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="01012345678"
                      {...register('phone')}
                    />
                    {errors.phone && (
                      <p className="text-sm text-destructive">
                        {errors.phone.message}
                      </p>
                    )}
                  </div>

                  {/* المحافظة */}
                  <div className="space-y-2">
                    <Label htmlFor="city">المحافظة *</Label>
                    <Select
                      onValueChange={(value) => setValue('city', value)}
                      defaultValue={watch('city')}
                    >
                      <SelectTrigger id="city">
                        <SelectValue placeholder="اختر المحافظة" />
                      </SelectTrigger>
                      <SelectContent>
                        {EGYPT_GOVERNORATES.map((gov) => (
                          <SelectItem key={gov} value={gov}>
                            {gov}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.city && (
                      <p className="text-sm text-destructive">
                        {errors.city.message}
                      </p>
                    )}
                  </div>

                  {/* العنوان */}
                  <div className="space-y-2">
                    <Label htmlFor="address">العنوان التفصيلي *</Label>
                    <Input
                      id="address"
                      placeholder="123 شارع الجمهورية، الدور الثالث"
                      {...register('address')}
                    />
                    {errors.address && (
                      <p className="text-sm text-destructive">
                        {errors.address.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">ملاحظات (اختياري)</Label>
                    <Textarea
                      id="notes"
                      placeholder="أي ملاحظات إضافية للتوصيل..."
                      rows={3}
                      {...register('notes')}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* طريقة الدفع */}
              <Card>
                <CardHeader>
                  <CardTitle>طريقة الدفع</CardTitle>
                  <CardDescription>
                    اختر طريقة الدفع المناسبة لك
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={paymentMethod}
                    onValueChange={(value) =>
                      setValue('paymentMethod', value as 'cash' | 'vodafone' | 'instapay')
                    }
                    className="space-y-4"
                  >
                    {/* الدفع عند الاستلام */}
                    <div className="flex items-center space-x-3 space-x-reverse p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                      <RadioGroupItem value="cash" id="cash" />
                      <Label
                        htmlFor="cash"
                        className="flex items-center gap-3 cursor-pointer flex-1"
                      >
                        <Truck className="w-5 h-5 text-muted-foreground" />
                        <div className="text-right flex-1">
                          <p className="font-medium">الدفع عند الاستلام</p>
                          <p className="text-sm text-muted-foreground">
                            ادفع نقداً عند استلام الطلب
                          </p>
                        </div>
                      </Label>
                    </div>

                    {/* فودافون كاش */}
                    <div className="flex items-center space-x-3 space-x-reverse p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                      <RadioGroupItem value="vodafone" id="vodafone" />
                      <Label
                        htmlFor="vodafone"
                        className="flex items-center gap-3 cursor-pointer flex-1"
                      >
                        <Phone className="w-5 h-5 text-muted-foreground" />
                        <div className="text-right flex-1">
                          <p className="font-medium">فودافون كاش</p>
                          <p className="text-sm text-muted-foreground">
                            حوّل على: {paymentNumber}
                          </p>
                        </div>
                      </Label>
                    </div>

                    {/* إنستاباي */}
                    <div className="flex items-center space-x-3 space-x-reverse p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                      <RadioGroupItem value="instapay" id="instapay" />
                      <Label
                        htmlFor="instapay"
                        className="flex items-center gap-3 cursor-pointer flex-1"
                      >
                        <MessageCircle className="w-5 h-5 text-muted-foreground" />
                        <div className="text-right flex-1">
                          <p className="font-medium">إنستاباي</p>
                          <p className="text-sm text-muted-foreground">
                            حوّل على: {paymentNumber}
                          </p>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                  {errors.paymentMethod && (
                    <p className="text-sm text-destructive mt-2">
                      {errors.paymentMethod.message}
                    </p>
                  )}

                  {/* ملاحظة للدفع الإلكتروني */}
                  {(paymentMethod === 'vodafone' || paymentMethod === 'instapay') && (
                    <div className="mt-4 p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        <strong>ملاحظة:</strong> بعد إتمام التحويل، يرجى التواصل معنا عبر واتساب على رقم{' '}
                        <span className="font-semibold text-foreground">{whatsappNumber}</span>{' '}
                        وإرسال صورة من إيصال التحويل.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ملخص الطلب */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>ملخص الطلب</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div key={item.product.id} className="flex gap-3">
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0 text-right">
                          <p className="font-medium text-sm truncate">
                            {item.product.name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            الكمية: {item.quantity}
                          </p>
                        </div>
                        <p className="font-medium text-sm">
                          {formatPrice(item.product.price * item.quantity)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">المجموع الفرعي</span>
                      <span>{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">الشحن</span>
                      <span className="font-medium">{formatPrice(shipping)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-lg font-semibold">
                      <span>الإجمالي</span>
                      <span className="text-primary">{formatPrice(total)}</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'جاري إتمام الطلب...' : 'إتمام الطلب'}
                  </Button>

                  {/* زر واتساب */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleWhatsAppContact}
                  >
                    <SiWhatsapp className="w-5 h-5 ml-2 text-[#25D366]" />
                    تواصل عبر واتساب
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
}